<template>
  <cs-preloader-overlay :loading="loading">
    <div class="digital-content-delivery">

      <digital-content-header
        :payment-id="getDisplayPaymentId"
        :language="getLanguage"
      />

      <div v-if="hasDisplayContent" class="content-container">
        <div class="iframe-content"
          v-if="hasDisplayContentInsidePage"
          v-html="digitalContent"
        ></div>

        <div v-else class="text-content">
          <a
            :href="digitalContent"
            target="_blank"
            rel="noopener noreferrer"
          >
            {{ $t('get.access') }}
          </a>

          <p v-html="$t('new.content')"></p>
          <DigitalContentAnimation />
        </div>
      </div>

      <div class="footer-container">
        <p
          v-html="$t('questions.to.recipient', { email: getDisplayMerchantEmail, getDisplayProjectName })"
        />
        <button
          v-if="getSuccessUrl"
          @click.prevent="handleRedirectByProject"
        >
          {{ $t('return.to.site') }}
        </button>
        <img :src="getLogoLink" alt="" />
      </div>

      <template v-if="analyticsCode">
        <div v-html="analyticsCode"></div>
      </template>
    </div>
  </cs-preloader-overlay>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';

// import logrocket from '@/services/logrocket';
import HTTPService from '@/services/Http';

import DigitalContentHeader from 'organisms/project/DigitalContentHeader';
import DigitalContentAnimation from '@/resources/tilda/DigitalContentAnimation';

const IFRAME_TAG_REGEX = /^<iframe.+<\/iframe>$/;
const HTTPS_REGEX = /https:\/\//;

const WHATSAPP_DOMAIN_REGEX = /chat.whatsapp.com|wa.me/;
const GOOGLE_DRIVE_DOMAIN_REGEX = /drive.google/;
const MAIL_CLOUD_DOMAIN_REGEX = /cloud.mail/;
const YANDEX_DRIVE_DOMAIN_REGEX = /yadi.sk/;
const YANDEX_DISK_DOMAIN_REGEX = /disk.yandex.ru/;
const VK_DOMAIN_REGEX = /vk.(com|me)\/(?!doc)/;
const TG_DOMAIN_REGEX = /t.me/;
const ONEDRIVE_REGEX = /1drv.ms/;
const TELEGRAPH_REGEX = /telegra.ph/;
const SHAREPOINT_REGEX = /sharepoint.com/;
const JOIN_CLUB_HOUSE_REGEX = /joinclubhouse.com/;
const NOTION_REGEX = /notion.so/;
const KNIGA_GAYD_REGEX = /kniga-gayd.ru/;
const DROPBOX_REGEX = /dropbox.com/;

const YOUTUBE_REGEX = /youtu|youtube/;
const PDF_REGEX = /.pdf/;

const EXCEPTIONS_LIST = [
  YANDEX_DISK_DOMAIN_REGEX,
  YANDEX_DRIVE_DOMAIN_REGEX,
  GOOGLE_DRIVE_DOMAIN_REGEX,
  MAIL_CLOUD_DOMAIN_REGEX,
  WHATSAPP_DOMAIN_REGEX,
  VK_DOMAIN_REGEX,
  TG_DOMAIN_REGEX,
  ONEDRIVE_REGEX,
  TELEGRAPH_REGEX,
  SHAREPOINT_REGEX,
  JOIN_CLUB_HOUSE_REGEX,
  NOTION_REGEX,
  KNIGA_GAYD_REGEX,
  DROPBOX_REGEX,
];

export default {
  name: 'PaidDigitalSelling',
  components: {
    DigitalContentHeader,
    DigitalContentAnimation,
  },
  created() {
    this.loadData();
  },
  data() {
    return {
      loading: true,
      digitalContent: null,
      analyticsCode: null,
    };
  },
  computed: {
    passwordCard() {
      return sessionStorage.getItem('passwordCard') || this.TRANSACTION.externalDigitalContentCode;
    },
    transactionId() {
      return this.getPaymentId || sessionStorage.getItem('transactionId');
    },
    hasValidRequestData() {
      const isValidPassword = this.$validate.isNotEmptyString(this.passwordCard);
      const isValidTransactionId = this.$validate.isNotEmptyString(this.transactionId);

      return isValidPassword && isValidTransactionId;
    },
    hasDisplayContent() {
      return this.$validate.isNotNull(this.digitalContent);
    },

    /**
     * Показываем контент на текущей странице:
     *  если iframe
     *  если https
     *  если нет в исключениях
    */
    hasDisplayContentInsidePage() {
      const { digitalContent } = this;

      // eslint-disable-next-line no-mixed-operators
      return this.checkOnIframeTag(digitalContent) || this.checkOnApplicationPdf(digitalContent) && this.checkOnHttpsProtocol(digitalContent) && !this.checkOnIncludeExceptions(digitalContent);
    },

    // TODO: CPME-4085 доработать метод в геттерах для получения текущего объекта подписки
    checkSubscription() {
      const { billTariff } = this.TRANSACTION;

      if (!this.$validate.isObjectWithFields(billTariff, ['subscription'])) {
        return false;
      }

      if (this.$validate.isNull(billTariff.subscription)) {
        return false;
      }

      return true;
    },
    ...mapState(['TRANSACTION', 'IS_IOS']),
    ...mapGetters([
      'getSuccessUrl',
      'getPaymentId',
      'getDisplayPaymentId',
      'getDisplayProjectName',
      'getDisplayMerchantEmail',
      'getLanguage',
      'getProjectCode',
      'getLogoLink',
    ]),
  },
  methods: {
    handleRedirectByProject() {
      this.$openLink(this.getSuccessUrl);
    },
    checkOnIframeTag(value) {
      return IFRAME_TAG_REGEX.test(value);
    },
    checkOnApplicationPdf(value) {
      return PDF_REGEX.test(value);
    },
    checkOnHttpsProtocol(value) {
      return HTTPS_REGEX.test(value);
    },
    checkOnYoutubeSource(value) {
      return YOUTUBE_REGEX.test(value);
    },
    checkOnIncludeExceptions(value) {
      return EXCEPTIONS_LIST.some(exception => exception.test(value));
    },
    wrapSourceByIframeTag(source) {
      const iframeParams = 'frameborder=\'0\' allow=\'accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture\' allowfullscreen';

      // eslint-disable-next-line no-useless-escape
      return `<iframe id='digitalContentIframe' ${iframeParams} src=\"${source}\"></iframe>`;
    },
    wrapSourceByApplicationPdf(source) {
      const viewerUrl = `https://docs.google.com/gview?url=${encodeURIComponent(source)}&embedded=true`;

      if (this.IS_IOS) {
        return this.wrapSourceByIframeTag(viewerUrl);
      }

      // return `<object data=\"${source}\" type='application/pdf' width='100%' height='100%'></object>`;
      return this.wrapSourceByIframeTag(source);
    },
    prepareSourceYoutubeByIframe(value) {
      const contentId = (/watch/.test(value)) ? value.split('?v=').slice(-1)[0] : value.replace(/(^.*\.be\/)/g, '');
      const preparedSource = this.wrapSourceByIframeTag(`https://www.youtube.com/embed/${contentId}`);

      return preparedSource;
    },
    prepareContent(content) {
      if (this.checkOnIframeTag(content)) {
        this.digitalContent = content;
        return;
      }

      if (this.checkOnIncludeExceptions(content)) {
        this.digitalContent = content;
        return;
      }

      if (this.checkOnYoutubeSource(content)) {
        this.digitalContent = this.prepareSourceYoutubeByIframe(content);
        return;
      }

      if (this.checkOnApplicationPdf(content)) {
        this.digitalContent = this.wrapSourceByApplicationPdf(content);
        return;
      }

      if (this.checkOnHttpsProtocol(content)) {
        this.digitalContent = this.wrapSourceByIframeTag(content);
        return;
      }

      this.digitalContent = content;
    },
    async loadData() {
      await this.loadContent();
      await this.GET_PROJECT_LOCALE({ publicId: this.transactionId });
      this.loadAnalytics();
    },
    async loadContent() {
      if (!this.hasValidRequestData) {
        this.$router.push({ name: 'service.error' });
        return;
      }

      const requestData = {
        paymentId: this.transactionId,
        password: this.passwordCard,
      };

      try {
        const { data } = await HTTPService.getDigitalContentAccess(requestData);

        if (this.$validate.isEmptyString(data.link)) {
          this.$router.push({ name: 'service.error' });
          return;
        }

        if (this.checkSubscription) {
          sessionStorage.setItem('paymentId', this.getPaymentId);
          sessionStorage.setItem('passwordCard', data.externalDigitalContentCode);
          sessionStorage.setItem('accessToken', data.subscriptionToken);
        }

        this.prepareContent(data.link);
      } catch (error) {
        console.log(error);

        this.$router.push({
          name: 'BillContentSecure',
          params: { id: this.transactionId },
        });
      }

      this.loading = false;
    },
    async loadAnalytics() {
      try {
        const { data } = await HTTPService.getProjectAnalytics(this.getProjectCode);

        if (data.status !== 'ACTIVE') {
          return;
        }

        this.analyticsCode = data.code;
      } catch (error) {
        // logrocket.logger(error);
      }
    },
    ...mapActions(['GET_PROJECT_LOCALE']),
  },
};
</script>

<style lang="sass">
  .digital-content-delivery
    iframe
      width: 100%
      height: 100%
      border: none
</style>

<style lang="sass" scoped>
  $indent: 10px

  .digital-content-delivery
    min-height: 100vh
    max-height: 100vh
    display: flex
    flex-flow: column
    padding-left: $indent
    padding-right: $indent
    font-family: 'Montserrat', Arial, sans-serif
    font-weight: 400
    color: rgba(33, 33, 33, 0.5)

  .content-container
    height: 80vh
    min-height: 80vh
    position: relative
    display: flex
    justify-content: center
    align-items: center
    flex-flow: column
    flex-grow: 1
    border-radius: $indent
    overflow: hidden
    background-color: #fff
    box-shadow: 0px 1px 2px rgba(189, 189, 189, 0.5)

    p
      width: 200px
      margin: 0 auto
      text-align: center
      font-size: 12px
      line-height: 140%

      &:not(:last-child)
        margin-bottom: 1.6rem

    a
      width: 100%
      height: 4rem
      display: flex
      align-items: center
      justify-content: center
      margin-bottom: 1.28rem
      border-radius: 2.48rem
      font-size: 18px
      font-weight: 600
      line-height: 22px
      color: #FFFFFF
      background-color: #7CB342
      transition: background-color .2s linear

      &:hover
        background-color: #6A963B

    img
      width: $indent * 10
      height: $indent * 10
      min-width: $indent * 10
      min-height: $indent * 10

    .text-content
      width: 240px
      z-index: 500

    .iframe-content
      width: 100%
      height: 100vh
      min-height: 100%
      z-index: 500

  .footer-container
    display: flex
    align-items: center
    justify-content: space-between
    padding: .4rem 0

    p
      font-size: 12px
      line-height: 140%
      color: rgba(33, 33, 33, 0.5)

    a
      color: #9B51E0

    button
      width: auto
      padding: 10px $indent * 5
      font-weight: 500
      font-size: 14px
      line-height: 17px
      color: #9B51E0
      white-space: nowrap
      background: rgba(155, 81, 224, 0.1)
      border-radius: 50px
      transition: background-color .2s linear

      &:hover
        background-color: rgba(155, 81, 224, 0.2)

    img
      max-width: 100%
      max-height: 100%
      display: none

    @media (max-width: 480px)
      display: block
      padding: 1rem 0
      text-align: center

      p
        margin-bottom: 1.36rem

      button
        margin-left: auto
        margin-right: auto
        margin-bottom: 1.36rem

      img
        width: 90px
        height: 56px
        display: block
        margin: 0 auto
</style>
